/**
 * The Transaction History drawer on the Play cockpit.
 *
 * A shell: it owns the two filters and nothing else. Fetching and merging the
 * seven ledgers is useGetTransactionHistoryRows; per-ledger display rules live
 * in TransactionHistoryRow.
 */

import React, {useState} from "react";
import CollapsibleDataCard from "@/components/play/CollapsibleDataCard";
import {useCockpitPreferences} from "@/hooks/play/useCockpitPreferences";
import {useGetTransactionHistoryRows} from "@/hooks/play/useGetTransactionHistoryRows";
import {TimeFilter, Token} from "@devour/client";
import {FaHistory} from "react-icons/fa";
import TransactionHistoryFilters from "./transactionHistory/TransactionHistoryFilters";
import TransactionHistoryRow from "./transactionHistory/TransactionHistoryRow";
import {TransactionFilter} from "./transactionHistory/transactionHistoryTypes";
import "./TransactionHistoryCard.scss";

interface TransactionHistoryCardProps {
    fullToken?: Token;
    userId?: string;
    defaultExpanded?: boolean;
    forceExpanded?: boolean;
}

const TransactionHistoryCard: React.FC<TransactionHistoryCardProps> = ({
    fullToken,
    userId,
    defaultExpanded = false,
    forceExpanded = false,
}) => {
    const {preferences, setTransactionFilter} = useCockpitPreferences();
    const [filter, setFilter] = useState<TransactionFilter>(preferences.transactionFilter as TransactionFilter || "xp");
    const [timeFilter, setTimeFilter] = useState<TimeFilter>(TimeFilter.day);

    const {rows, isLoading, isError} = useGetTransactionHistoryRows(fullToken, userId, filter, timeFilter);

    // The chosen ledger persists across sessions, so write it back to preferences.
    const handleFilterChange = (newFilter: TransactionFilter) => {
        setFilter(newFilter);
        setTransactionFilter(newFilter);
    };

    return (
        <CollapsibleDataCard
            id="transaction-history-card"
            title="Transaction History"
            icon={<FaHistory/>}
            defaultExpanded={defaultExpanded}
            forceExpanded={forceExpanded}
            isLoading={isLoading}
            hasData={rows.length > 0}
            className="transaction-history-card-wrapper"
        >
            <div className="transaction-history-card">
                <TransactionHistoryFilters
                    timeFilter={timeFilter}
                    onTimeFilterChange={setTimeFilter}
                    filter={filter}
                    onFilterChange={handleFilterChange}
                />

                <div className="transaction-history-card__list">
                    {isLoading
                        ? <div className="transaction-history-card__empty">
                            Loading...
                        </div>
                        : rows.length === 0
                            /*
                             * A failed fetch must not read as "you have none" —
                             * that is what sent a player to support convinced
                             * their wheel win had vanished.
                             */
                            ? <div className="transaction-history-card__empty">
                                {isError
                                    ? "Couldn't load your transactions. Try again in a moment."
                                    : "No transactions found"}
                            </div>
                            : rows.map((transaction) =>
                                <TransactionHistoryRow key={transaction.id} transaction={transaction}/>)
                    }
                </div>
            </div>
        </CollapsibleDataCard>
    );
};

export default React.memo(TransactionHistoryCard);
